<template>
  <!-- Figma "Service & Repair - Details": main layout, cards like the appointment detail -->
  <div class="w-full">
    <div v-if="isJobDetailCalled"
      class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[20px] pb-[10px] laptop:pb-[25px] largeDesktop:px-[30px] largeDesktop:pb-[35px] pt-[35px] border-gray-300">
      <DetailBackButton fallback="/work-order/service-and-repair" />
      <!-- actions: first 3 as pills, the rest in the ⋮ menu (ActionBar).
           laptop+: pills sit on the card's top edge; below laptop they get their own (wrapping) row -->
      <!-- pill / ⋮ look from Syed Arbaz (fbb970a): arrow icon in a white circle, 3-dots image button -->
      <ActionBar v-if="barActions.length" :actions="barActions"
        trigger-class="group w-[40px] h-[40px] rounded-full flex items-center justify-center transition-all duration-300 border-0 bg-transparent p-0"
        class="w-full laptop:px-2.5 absolute -top-[21px] right-0">
        <template #pill="{ action, run, narrow }">
          <button type="button" :disabled="action.disabled"
            class="group flex items-center rounded-full gap-2 py-1.5 pl-3 pr-2 font-roboto font-normal text-[15px] largeDesktop:text-[17px] h-[40px] border-1 bg-[#fff] whitespace-nowrap transition duration-300 hover:!text-white disabled:cursor-not-allowed disabled:opacity-[0.5]"
            :style="narrow
              ? { color: '#fff', borderColor: action.color, backgroundColor: action.color }
              : { color: action.color, borderColor: action.color }"
            @mouseover="!narrow && !action.disabled && ($event.currentTarget.style.backgroundColor = action.color)"
            @mouseleave="!narrow && ($event.currentTarget.style.backgroundColor = '#fff')" @click="run(action)">
            {{ action.label }}
            <span
              class="w-[26px] h-[26px] rounded-full flex items-center justify-center bg-white shadow-[0px_0px_15px_-7px_#333]">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  :stroke="action.color" d="M4 12H6.5M14 18L20 12L14 6M20 12H9.5" />
              </svg>
            </span>
          </button>
        </template>
        <template #trigger>
          <img src="../../assets/images/3-dots-icon.png" alt=""
            class="w-full h-full object-contain group-hover:hidden block [@media(max-width:1023px)]:!hidden">
          <img src="../../assets/images/3-dots-icon-hover.png" alt=""
            class="w-full h-full object-contain group-hover:block hidden [@media(max-width:1023px)]:!block">
        </template>
      </ActionBar>

      <div v-if="isConsumerQuoteDeclined"
        class="flex items-center justify-center gap-3 rounded-[10px] p-3 mb-4 bg-[#D7385E] text-white">
        <img src="../../assets/images/declined-icon.png" alt="declined-icon">
        <h3 class="m-0 text-[18px] font-roboto font-medium">Consumer has declined the quote</h3>
      </div>

      <!-- Android JobRequestFragment: pending job → Accept / Reject -->
      <div v-if="isPendingJob && !isReadOnly"
        class="flex mobile:flex-col tablet:flex-row tablet:items-center justify-between gap-3 rounded-[10px] p-3 mb-4 bg-[#FEF7F7] border-1 border-[#F67280] font-roboto">
        <div class="min-w-0">
          <h3 class="m-0 text-[18px] font-medium text-[#333333]">New service request</h3>
          <p class="m-0 mt-1 text-[14px] text-[#333333]">
            You have received a {{ jobDetail.services?.[0]?.title || 'service' }} job request.
            <span v-if="jobDetail.booking_date" class="whitespace-nowrap">Schedule: {{ jobDetail.booking_date
            }}<template v-if="bookingTimeText"> · {{ bookingTimeText }}</template></span>
          </p>
        </div>
        <div class="flex gap-[10px] flex-shrink-0">
          <button type="button" class="fxDeclineCancel" @click="rejectJob()">Reject</button>
          <button type="button" class="fxBtnPrimary" @click="acceptJob()">Accept</button>
        </div>
      </div>

      <div class="flex mobile:flex-col laptop:flex-row gap-[20px] items-start">
        <!-- left -->
        <div class="flex-1 min-w-0 w-full flex flex-col gap-[20px]">
          <div class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white">
            <div class="flex mobile:flex-col tablet:flex-row justify-between tablet:items-start gap-2 pb-[10px]">
              <div class="flex items-center gap-1">
                <div class="w-[8px] h-[5px] laptop:h-[8px] rounded-full bg-[#A084E8]" />
                <div class="w-[35px] h-[5px] laptop:h-[8px] rounded-full bg-[#A084E8]" />
                <p class="mobile:text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">
                  Job Details</p>
              </div>
              <div class="tablet:text-right">
                <div class="flex items-center mobile:justify-between tablet:justify-end gap-2">
                  <span class="text-[14px] tablet:text-[15px] font-roboto text-[#333333]">Job Status:</span>
                  <span
                    class="text-[12px] tablet:text-[13px] largeDesktop:text-[14px] font-medium font-roboto py-[3px] px-[12px] rounded-[5px] text-white bg-[#5E366A]"
                    :style="{ backgroundColor: $helper.sarStatusColor(jobDetail.status_text) }">
                    {{ getCapitalize(jobDetail.status_text) || '-' }}
                  </span>
                </div>
                <p v-if="getJobStatusDescription()"
                  class="m-0 mt-2 text-[12px] leading-[1.2] font-roboto text-[#999999]">{{
                    getJobStatusDescription() }}</p>
              </div>
            </div>

            <!-- this Qr box just for mob/tab -->
            <div v-if="is_QR" class="bg-white py-[10px] mobile:flex laptop:hidden justify-between items-center">
              <div>
                <span class="m-0 text-[12px] text-[#F67280]">Technician</span>
                <p class="m-0 text-[14px] font-semibold text-[#333333]">{{ jobDetail.technician.first_name }}</p>
              </div>
              <img :src="QRData.qrcodeUrl"
                class="w-[60px] h-[60px] object-contain rounded-[5px] p-[4px] shadow-[0px_0px_17px_-2px_#ccc]"
                alt="QR code">
            </div>

            <!-- below laptop (Figma mobile 5908-2374): label left / value right rows with thin dividers -->
            <div class="laptop:hidden flex flex-col text-[14px] font-roboto text-[#333333]">
              <div class="flex justify-between items-center gap-2 py-[8px] border-t border-[#EFEFEF]">
                <span>Job Date:</span>
                <span class="text-right">{{ jobDateText }}</span>
              </div>
              <div class="flex justify-between items-center gap-2 py-[8px] border-t border-[#EFEFEF]">
                <span>Service Type:</span>
                <span class="px-[10px] py-[2px] rounded-full text-white text-[12px] bg-[#7695FF]">{{
                  jobDetail.services?.[0]?.title || '-' }}</span>
              </div>
              <div class="flex justify-between items-center gap-2 py-[8px] border-y border-[#EFEFEF]">
                <span>Booking ID:</span>
                <span class="px-[10px] py-[2px] rounded-full text-white text-[12px] bg-[#5BC0BE]">{{ jobDetail.id
                }}</span>
              </div>
            </div>
            <div
              class="mobile:hidden laptop:flex flex-row justify-between items-center gap-2 p-2.5 border border-[#EFEFEF] rounded-[5px]">
              <p class="m-0 text-[14px] largeDesktop:text-[16px] font-roboto  text-[#333333]">
                Job Date: {{ jobDateText }}
              </p>
              <div class="flex items-center gap-3 flex-wrap">
                <span class="p-[5px] text-[14px] font-roboto rounded-full bg-[#EFEFEF]/50 text-[#333333]">Service Type:
                  <span class="ml-1 px-2 py-[2px] rounded-full text-white text-[13px] bg-[#7695FF]">{{
                    jobDetail.services?.[0]?.title
                    || '-' }}</span>
                </span>
                <span class="p-[5px] text-[14px] font-roboto rounded-full bg-[#EFEFEF]/50 text-[#333333]">Booking ID:
                  <span class="ml-1 px-2 py-[2px] rounded-full text-white text-[13px] bg-[#5BC0BE]">{{ jobDetail.id
                  }}</span>
                </span>
              </div>
            </div>
            <div v-if="jobDetail.address" class="mt-[15px] flex flex-col gap-[5px]">
              <span class="text-[14px] font-roboto text-[#EA8A8A]">Service Address:</span>
              <p class="m-0 text-[14px] largeDesktop:text-[16px] font-roboto text-[#333333]">{{
                jobDetail.address.address || '-' }}
              </p>
            </div>
          </div>

          <!-- Figma F2 / F4 / F6: submitted / approved quote (G-18) -->
          <JobQuoteCard :booking-id="$route.params.id" :share-url="is_QR ? (QRData?.jobStateUrl || '') : ''"
            :consumer="jobDetail.consumer" />

          <!-- Figma G1: work checklist while the repair is in progress (End Job available) -->
          <WorkChecklist v-if="showApprovedWork && hasEndJob" :booking-id="$route.params.id" :can-complete="true"
            @complete="actionClicked('End Job')" />

          <div class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] p-[15px] bg-white">
            <nest_gallery />
          </div>
        </div>

        <!-- right -->
        <div class="w-full laptop:w-[320px] largeDesktop:w-[350px] laptop:flex-shrink-0 flex flex-col gap-[20px]">
          <nest_consumer_detail />

          <div v-if="jobDetail.depp_schedule && jobDetail.is_partner_job == 1"
            class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] p-[15px] bg-white">
            <div class="flex items-center gap-1 pb-[15px]">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
              <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">
                {{ jobDetail.is_from_nest ? 'fyxify Contact Details' : 'Partner Details' }}
              </p>
            </div>
            <div class="flex flex-col items-center gap-2">
              <img
                :src="jobDetail.depp_schedule.show_consumer_as_resident ? require('@/assets/images/fyxifyLogo.png') : jobDetail.depp_schedule.company_logo"
                :alt="jobDetail.depp_schedule.company_name" class="h-[50px] object-contain">
              <a :href="'tel:' + jobDetail.depp_schedule.phone" class="no-underline text-[#168EFF]">{{
                jobDetail.depp_schedule.phone }}</a>
            </div>
          </div>

          <div class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] p-[15px] bg-white">
            <div class="flex items-center gap-1 pb-[15px]">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
              <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">Internal Notes
              </p>
            </div>
            <nest_notes v-if="bookingNotes" :is-from-job-detail="true" />
          </div>

          <MapCard v-if="Number(jobDetail.lat)" :lat="jobDetail.lat" :lng="jobDetail.long"
            :address="jobDetail.address?.address || ''" />

          <div v-if="is_QR"
            class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] p-[15px] mobile:hidden laptop:block bg-white">
            <div class="flex items-center gap-1 pb-[15px]">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
              <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">QR Code</p>
            </div>
            <img :src="QRData.qrcodeUrl" class="mx-auto block w-[140px] h-[140px] object-contain" alt="QR code">
            <p class="m-0 mt-2 text-[13px] font-roboto text-[#333333] text-center">
              Present this <span class="text-[#FE5000]">QR code</span> to the customer to view the quote details.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div id="cancelBookingModal" ref="cancelBookingModal" class="modal fade fxModal" tabindex="-1"
      aria-labelledby="exampleModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <!-- Figma "F5 · Decline Quote (Modal)" -->
      <div class="modal-dialog modal-dialog-centered" style="max-width: 440px">
        <div class="modal-content">
          <div class="modal-body p-[20px]">
            <FxModalHeader title="Decline Quote" />
            <nest_cancel_booking :booking="jobDetail" :mark-as-complete-object="markAsCompleteObject"
              @event="cancelCallback" />
          </div>
        </div>
      </div>
    </div>

    <!-- Suspend Job (S&R modal style) -->
    <div id="suspendJobModal" ref="suspendJobModal" class="modal fade fxModal fxConfirm" tabindex="-1"
      aria-labelledby="exampleModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 440px">
        <div class="modal-content">
          <div class="modal-body">
            <ConfirmLayout title="Suspend Job" bs-dismiss>
              Are you sure you want to suspend this job?<br>Please enter a reason for suspension to continue.
              <label class="block m-0 mt-[15px] text-left leading-normal">
                <span class="block mb-[5px] text-[13px] font-medium text-[#333333]">Reason For Suspension</span>
                <textarea id="addReasonsTextarea" v-model="suspendReason" rows="3" placeholder="Enter reason here..."
                  class="w-full px-[12px] py-[10px] rounded-[10px] border-1 outline-none text-[14px] text-[#333333] resize-none"
                  :class="$helper.isEmpty(suspendReason) && isValidate ? 'border-[#F67280] bg-[#FEF7F7]' : 'border-[#EFEFEF]'" />
                <span v-if="$helper.isEmpty(suspendReason) && isValidate"
                  class="block mt-[4px] text-[12px] text-[#EA8A8A]">This field is required.</span>
              </label>
              <template #actions>
                <button type="button" class="fxConfirmBtnOutline" data-bs-dismiss="modal"
                  aria-label="Close">Dismiss</button>
                <button type="button" class="fxConfirmBtn" @click="suspendJobBtn()">Suspend Job</button>
              </template>
            </ConfirmLayout>
          </div>
        </div>
      </div>
    </div>

    <!-- View Evaluation / Approved Quote (external page in an iframe) -->
    <div id="iframeModal" ref="iframeModal" class="modal fade fxModal" tabindex="-1" aria-labelledby="exampleModalLabel"
      aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-xl">
        <div class="modal-content">
          <div class="modal-body p-[20px]">
            <FxModalHeader :title="techUrl && techUrl.includes('status=0') ? 'Approved Quote' : 'View Evaluation'" />
            <div class="modalCntnt pt-[15px]">
              <iframe :src="techUrl" class="w-full rounded-[10px] border-1 border-[#EFEFEF]" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Consumer details (mobile) -->
    <div id="profileModal" ref="profileModal" class="modal fade fxModal" tabindex="-1"
      aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 440px">
        <div class="modal-content">
          <div class="modal-body p-[20px]">
            <FxModalHeader title="Consumer Details" />
            <div class="modalCntnt pt-[15px]">
              <nest_consumer_detail v-if="isMobile" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact fyxify -->
    <div id="partnerModal" ref="partnerModal" class="modal fade fxModal" tabindex="-1"
      aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 400px">
        <div class="modal-content">
          <div class="modal-body p-[20px] font-roboto">
            <FxModalHeader title="fyxify Contact Details" />
            <div v-if="jobDetail.depp_schedule"
              class="flex flex-col items-center gap-[15px] mt-[20px] px-[15px] py-[20px] rounded-[10px] border-1 border-[#EFEFEF]">
              <img :src="jobDetail.depp_schedule.company_logo" class="max-w-[160px] max-h-[70px] object-contain"
                :alt="jobDetail.depp_schedule.company_name">
              <a :href="'tel:' + jobDetail.depp_schedule.phone" target="_blank"
                class="flex items-center gap-[8px] no-underline text-[15px] font-medium text-[#6C79E0]">
                <span class="w-[30px] h-[30px] rounded-full flex items-center justify-center bg-[#F67280]">
                  <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#FFFFFF"
                      d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z" />
                  </svg>
                </span>
                {{ jobDetail.depp_schedule.phone }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Send Proposal / Payment Acknowledgement (Android SendProposalDialogue / PaymentInfoDialog) -->
    <SendProposalModal ref="sendProposalModal" :booking-id="$route.params.id"
      @sent="onActionSuccess('Evaluation report has been emailed successfully.', false)" @failed="handleFailure" />
    <!-- Android CompletedJobsFragment (Job History) -->
    <JobHistoryModal ref="jobHistoryModal" :booking-id="$route.params.id" @failed="handleFailure" />

    <!-- Android JobLandingFragment "View Linked Jobs" (⋮) → "Select Linked Job" when there are several -->
    <div id="linkedJobsModal" ref="linkedJobsModal" class="modal fade fxModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 400px">
        <div class="modal-content">
          <div class="modal-body p-[20px] font-roboto">
            <FxModalHeader title="Select Linked Job" />
            <div class="mt-[15px] flex flex-col gap-[10px]">
              <!-- real <a href> (open in new tab); a plain click also closes the modal -->
              <router-link v-for="job in linkedJobs" :key="job.booking_id" :to="linkedJobRoute(job.booking_id)"
                class="block w-full text-left no-underline px-[15px] py-[12px] rounded-[10px] border-1 border-[#EFEFEF] bg-[#fff] hover:bg-[#F4F5FB] text-[15px] font-medium text-[#333333] transition"
                @click="onLinkedJobLinkClick">
                Booking #{{ job.booking_id }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PaymentAcknowledgementModal ref="paymentAckModal" :booking-id="$route.params.id"
      :consumer-name="jobDetail.consumer?.first_name || ''" @done="onActionSuccess('Payment acknowledgement saved.')"
      @failed="onPaymentAckFailed" />

    <!-- QR Code (mobile) -->
    <!-- Figma "F1 · Evaluation Submitted (Success)": opened once after Review & Submit (history state from summary-detail.vue) -->
    <div ref="evalSubmittedModal" id="evalSubmittedModal" class="modal fade fxConfirm" tabindex="-1" aria-hidden="true">
      <div
        class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
        <div class="modal-content">
          <div class="modal-body p-[20px] font-roboto">
            <FxModalHeader :title="evalSubmitted.title" />
            <div class="flex flex-col items-center gap-[12px] mt-[20px]">

              <div class="flex justify-center mt-[28px] mb-[20px]">
                <span class="w-[84px] h-[84px] rounded-full bg-[#E7F7F0] flex items-center justify-center">
                  <span class="w-[58px] h-[58px] rounded-full bg-[#1FA971] flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="30px" height="30px" viewBox="0 0 64 64">
                      <path d="M0 0h64v64H0z" fill="none" />
                      <path fill="#fff" d="M56 2L18.8 42.909L8 34.729H2L18.8 62L62 2z" />
                    </svg>

                  </span>
                </span>
              </div>
              <p class="m-0 text-center text-[14px] leading-[20px] text-[#5B616B]">
                {{ evalSubmitted.quoteNumber ? 'Quote ' + evalSubmitted.quoteNumber : 'The quote' }} has been created
                and
                sent
                to {{ capitalizeFirst(jobDetail.consumer?.first_name || '') || 'the consumer' }} for approval.
              </p>
              <div class="mt-[18px] rounded-[12px] bg-[#F7F8FA] px-[14px] text-[13px] w-full">
                <div class="flex justify-between gap-[10px] py-[10px] border-b-[1px] border-[#E7EAEF]">
                  <span class="text-[#5B616B]">Quote total</span>
                  <span class="font-semibold text-[#2E3138]">{{ evalSubmitted.total || '-' }}</span>
                </div>
                <!-- one unit per booking -->
                <div class="flex justify-between gap-[10px] py-[10px] border-b-[1px] border-[#E7EAEF]">
                  <span class="text-[#5B616B]">Units evaluated</span>
                  <span class="font-semibold text-[#2E3138]">1 of 1</span>
                </div>
                <div class="flex justify-between gap-[10px] py-[10px]">
                  <span class="text-[#5B616B]">Status</span>
                  <span class="font-semibold text-[#2E3138]">Awaiting consumer approval</span>
                </div>
              </div>
            </div>
          </div>
          <!-- <div class="modal-body font-roboto">
            <div class="p-[15px]">
              <div class="flex items-start justify-between gap-[10px]">
                <h3 class="m-0 text-[20px] leading-[28px] font-semibold text-[#2E3138]">{{ evalSubmitted.title }}</h3>
                <button type="button" aria-label="Close"
                  class="w-[36px] h-[36px] -mt-[4px] -mr-[6px] flex-shrink-0 flex items-center justify-center border-0 bg-transparent p-0"
                  @click="closeEvalSubmitted()">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M6 6l12 12M18 6 6 18" stroke="#FF0004" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </button>
              </div>
              <div class="flex justify-center mt-[28px] mb-[20px]">
                <span class="w-[84px] h-[84px] rounded-full bg-[#E7F7F0] flex items-center justify-center">
                  <span class="w-[58px] h-[58px] rounded-full bg-[#1FA971] flex items-center justify-center">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#FFFFFF" stroke-width="2.6" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </span>
                </span>
              </div>
              <p class="m-0 text-center text-[14px] leading-[20px] text-[#5B616B]">
                {{ evalSubmitted.quoteNumber ? 'Quote ' + evalSubmitted.quoteNumber : 'The quote' }} has been created
                and
                sent
                to {{ capitalizeFirst(jobDetail.consumer?.first_name || '') || 'the consumer' }} for approval.
              </p>
              <div class="mt-[18px] rounded-[8px] bg-[#F7F8FA] px-[14px] text-[13px]">
                <div class="flex justify-between gap-[10px] py-[10px] border-b-1 border-[#E9ECF1]">
                  <span class="text-[#5B616B]">Quote total</span>
                  <span class="font-medium text-[#2E3138]">{{ evalSubmitted.total || '-' }}</span>
                </div>
                <div class="flex justify-between gap-[10px] py-[10px] border-b-1 border-[#E9ECF1]">
                  <span class="text-[#5B616B]">Units evaluated</span>
                  <span class="font-medium text-[#2E3138]">1 of 1</span>
                </div>
                <div class="flex justify-between gap-[10px] py-[10px]">
                  <span class="text-[#5B616B]">Status</span>
                  <span class="font-medium text-[#2E3138]">Awaiting consumer approval</span>
                </div>
              </div>
            </div>
          </div> -->
        </div>
      </div>
    </div>
    <div v-if="isMobile" id="qrModal" ref="qrModal" class="modal fade fxModal" tabindex="-1"
      aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 400px">
        <div class="modal-content">
          <div class="modal-body p-[20px] font-roboto">
            <FxModalHeader title="QR Code" />
            <div v-if="is_QR" class="flex flex-col items-center gap-[12px] mt-[20px]">
              <img :src="QRData.qrcodeUrl" class="w-[180px] h-[180px] object-contain" :alt="QRData.qrcodeUrl">
              <p class="m-0 text-center text-[13px] text-[#333333]">
                Present this <span class="text-[#FE5000]">QR code</span> to the customer to view the quote details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DetailBackButton from "@/components/partialComponent/DetailBackButton.vue";
import { evaluationApi } from "@/services/evaluationApi";
import nest_consumer_detail from "../../components/nest_components/nest_consumer_detail.vue";
import nest_notes from "../../components/nest_components/nest_notes.vue";
import nest_gallery from "../../components/nest_components/nest_gallery.vue";
import nest_cancel_booking from "../../components/nest_components/nest_cancel_booking.vue";
import FxModalHeader from "../../components/nest_components/FxModalHeader.vue";
import ConfirmLayout from "../../components/confirmDialog/ConfirmLayout.vue";
import WorkChecklist from "../../components/nest_components/WorkChecklist.vue";
import JobQuoteCard from "../../components/nest_components/JobQuoteCard.vue";
import SendProposalModal from "../../components/booking/SendProposalModal.vue";
import PaymentAcknowledgementModal from "../../components/booking/PaymentAcknowledgementModal.vue";
import JobHistoryModal from "../../components/booking/JobHistoryModal.vue";
import ActionBar from "../../components/partialComponent/ActionBar.vue";
import MapCard from "../../components/partialComponent/MapCard.vue";
import { bookingActionsApi, BOOKING_STATUS } from "../../services/bookingActionsApi";
import { mapActions, mapGetters } from "vuex";
import { Modal } from "bootstrap";
import { disableScroll, enableScroll } from "@/helpers/scrollHelper";
import { isNewTabClick } from "@/helpers/newTabClick";
import { isSilentLogin } from "@/helpers/embedMode";
import { useToast } from "vue-toastification";
import { faL } from "@fortawesome/free-solid-svg-icons";
const toast = useToast();

export default {
  data() {
    return {
      // Approved Work (WorkChecklist) is off for now: set true when it ships
      showApprovedWork: false,
      // Figma F1 popup after an evaluation was submitted / updated
      evalSubmitted: { title: "", total: "" },
      isLaptop: window.innerWidth >= 1024,
      actions: [],
      is_QR: false,
      isDeclineQuoteEnable: false,
      isJobDetailCalled: false,
      isConsumerQuoteDeclined: false,
      techUrl: "",
      screenWidth: "",
      showCreateVerbage: false,
      suspendReason: '',
      isValidate: false,
      // Android shows the payment acknowledgement dialog automatically once per job load
      paymentAckShownFor: null,
      // Android "Unpaid Invoice(s)" alert on a completed, unpaid job — once per job load
      unpaidAlertShownFor: null,
    };
  },
  computed: {
    // Figma: a few action pills on top, the rest in the ⋮ menu
    // Job Date = when the job was created (created_at, unix seconds), "08/27/2026 · 08:00 AM"
    jobDateText() {
      const created = this.jobDetail?.created_at;
      return Number(created) > 0 ? this.$helper.formatDateTimestamp(created, "MM/DD/YYYY · hh:mm A") : "-";
    },
    // Figma: "08/27/2026 · 08:00 AM" (booking_time is "HH:mm")
    bookingTimeText() {
      const time = this.jobDetail?.booking_time;
      if (!time || !/^\d{1,2}:\d{2}/.test(time)) return '';
      const [h, m] = time.split(':').map(Number);
      return `${String(h % 12 || 12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
    },
    hasEndJob() {
      return (this.actions || []).some(action => this.actionName(action) === 'End Job');
    },
    // ONE ordered list of every visible action; ActionBar shows the first 3 as pills and the rest in ⋮
    // (Decline Quote / Cancel Job / Suspend Job are 'danger' → ordered after the normal actions).
    // Contact fyxify / QR Code are only offered below laptop (on laptop they are cards on the page).
    barActions() {
      const danger = ['Cancel Job', 'Suspend Job'];
      const list = (this.actions || []).map(item => {
        const name = this.actionName(item);
        return {
          key: name,
          label: name,
          color: this.actionColor(item),
          originalImg: item?.originalImg,
          hoverImg: item?.hoverImg,
          variant: danger.includes(name) ? 'danger' : undefined,
          onClick: () => this.actionClicked(name),
        };
      });
      if (this.isDeclineQuoteEnable) {
        list.push({ key: 'Decline Quote', label: 'Decline Quote', color: '#D7385E', variant: 'danger', onClick: () => this.declineCallButtonClicked() });
      }
      if (!this.isLaptop && this.jobDetail?.is_partner_job == 1) {
        list.push({ key: 'Contact fyxify', label: 'Contact fyxify', color: '#4E7BFF', onClick: () => this.showBsModal('partnerModal') });
      }
      if (!this.isLaptop && this.is_QR) {
        list.push({ key: 'QR Code', label: 'QR Code', color: '#4E7BFF', onClick: () => this.showBsModal('qrModal') });
      }
      return list;
    },
    // Android JobLandingFragment(..., disableJobHistoryButton = true) when the job is opened from Job History
    isFromJobHistory() {
      return String(this.$route.query.history || '') === '1';
    },
    linkedJobs() {
      return Array.isArray(this.jobDetail?.linked_jobs) ? this.jobDetail.linked_jobs : [];
    },
    // Android: replacement_quote.is_consumer_requested_pricing → "Installation Pricing"
    hasInstallationPricing() {
      return !!this.jobDetail?.replacement_quote?.is_consumer_requested_pricing;
    },
    // Android JobLandingFragment: a job assigned to another technician is read-only
    isReadOnly() {
      let me = null;
      try {
        me = JSON.parse(localStorage.getItem('user'))?.user?.user_id;
      } catch (e) {
        me = null;
      }
      const owner = this.jobDetail?.technician?.user_id ?? this.jobDetail?.technician_id;
      if (me == null || owner == null || owner === '') return false;
      return String(me) !== String(owner);
    },
    isPendingJob() {
      return this.jobDetail?.status !== undefined && this.jobDetail?.status !== null && this.jobDetail?.status !== ''
        && Number(this.jobDetail.status) === BOOKING_STATUS.PENDING;
    },
    // BookingDetailResource: acknowledgement = REQUIRED | NOT_REQUIRED | ACKNOWLEDGED | NOT_ACKNOWLEDGED
    isPaymentAckRequired() {
      return this.jobDetail?.acknowledgement === 'REQUIRED' && !!this.jobDetail?.consumer;
    },
    isSuperPro() {
      const fromMenu = this.dashboardData?.is_super_pro ?? this.sideMenuData?.is_super_pro;
      if (fromMenu !== undefined && fromMenu !== null) return !!fromMenu;
      try {
        const roles = JSON.parse(localStorage.getItem('user'))?.user?.roles;
        return Array.isArray(roles) && roles.includes('technician_master');
      } catch (e) {
        return false;
      }
    },
    isSilentLogin() {
      return isSilentLogin();
    },
    ...mapGetters([
      "jobDetail",
      "apiCallback",
      "bookingNotes",
      "QRData",
      "bookingDiagnosisItems",
      "storeChecksResponse",
      "updateJobStatusResponse",
      "resumeJobResponse",
      "endJobResponse",
      "markAsCompleteObject",
      "resubmitQuoteResponse",
      "createInvoiceResponse",
      "invoiceList",
      "dashboardData",
      "sideMenuData"
    ]),

    isMobile() {
      return window.screen.width < 576 ? true : false;
    },
  },

  components: {
    MapCard,
    DetailBackButton,
    nest_consumer_detail,
    nest_notes,
    nest_gallery,
    nest_cancel_booking,
    FxModalHeader,
    ConfirmLayout,
    WorkChecklist,
    JobQuoteCard,
    SendProposalModal,
    PaymentAcknowledgementModal,
    JobHistoryModal,
    ActionBar,
  },

  watch: {
    // Suspend Job (status 8) is the only caller here
    updateJobStatusResponse: function () {
      toast.success("Job has been suspended successfully.");
      if (this.modal) {
        this.modal.hide();
      }
      this.getBookingDetail({
        booking_id: this.$route.params.id,
      });
    },

    resubmitQuoteResponse: function () {
      toast.success("Quote has been Resubmitted successfully.");
      this.getBookingDetail({
        booking_id: this.$route.params.id,
      });
    },

    endJobResponse: function () {
      toast.success("Job has been ended successfully.");
      this.getBookingDetail({
        booking_id: this.$route.params.id,
      });
    },

    resumeJobResponse: function () {
      toast.success("Job has been resumed successfully.");
      this.getBookingDetail({
        booking_id: this.$route.params.id,
      });
    },

    jobDetail: function () {
      this.actions = this.getJobStatusWithDescriptionForJobStatus("actions");
      this.setQR();
      this.isJobDetailCalled = true;
      this.autoShowPaymentAck();
      this.autoShowUnpaidAlert();
    },

    // e.g. the active-record guard opening another booking from this page
    "$route.params.id": function (id, oldId) {
      if (!id || id === oldId || this.$route.path.indexOf('/job/detail/') !== 0) return;
      this.paymentAckShownFor = null;
      this.unpaidAlertShownFor = null;
      this.getData();
      this.getNotesData();
    },

    storeChecksResponse: function () {
      this.$router.push({
        path: `/edit-summary/${this.$route.params.id}`,
      });
    },

    markAsCompleteObject: function () {
      this.modal = new Modal(this.$refs.cancelBookingModal);
      this.modal.show();
    },

    createInvoiceResponse: function () {
      this.$router.push({
        path: `/checkout/invoice/${this.createInvoiceResponse.invoice_id}`
      });
    },
  },

  mounted() {
    window.addEventListener('resize', this.onResize);
    this.openEvalSubmitted();
    this.screenWidth = screen.width;
    this.getBookingDetails({
      booking_id: this.$route.params.id,
    });
    this.getData();
    this.getNotesData();

    const modalElement = document.getElementById("cancelBookingModal");
    modalElement.addEventListener("show.bs.modal", this.addOverflowHidden);
    modalElement.addEventListener("hidden.bs.modal", this.removeOverflowHidden);


    const suspendModalElement = document.getElementById("suspendJobModal");
    suspendModalElement.addEventListener("show.bs.modal", this.addOverflowHidden);
    suspendModalElement.addEventListener("hidden.bs.modal", this.removeOverflowHidden);

    if (this.isMobile) {
      const qrModalElement = document.getElementById("qrModal");
      qrModalElement.addEventListener("show.bs.modal", this.addOverflowHidden);
      qrModalElement.addEventListener("hidden.bs.modal", this.removeOverflowHidden);

      const consumerModalElement = document.getElementById("profileModal");
      consumerModalElement.addEventListener("show.bs.modal", this.addOverflowHidden);
      consumerModalElement.addEventListener("hidden.bs.modal", this.removeOverflowHidden);

      const partnerModalElement = document.getElementById("partnerModal");
      partnerModalElement.addEventListener("show.bs.modal", this.addOverflowHidden);
      partnerModalElement.addEventListener("hidden.bs.modal", this.removeOverflowHidden);
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    const modalElement = document.getElementById("cancelBookingModal");
    modalElement.removeEventListener("show.bs.modal", this.addOverflowHidden);
    modalElement.removeEventListener(
      "hidden.bs.modal",
      this.removeOverflowHidden
    );

    const suspendModalElement = document.getElementById("suspendJobModal");
    suspendModalElement.removeEventListener("show.bs.modal", this.addOverflowHidden);
    suspendModalElement.removeEventListener(
      "hidden.bs.modal",
      this.removeOverflowHidden
    );

    if (this.isMobile) {
      const consumerModalElement = document.getElementById("profileModal");
      consumerModalElement.removeEventListener("show.bs.modal", this.addOverflowHidden);
      consumerModalElement.removeEventListener(
        "hidden.bs.modal",
        this.removeOverflowHidden
      );

      const partnerModalElement = document.getElementById("partnerModal");
      partnerModalElement.removeEventListener("show.bs.modal", this.addOverflowHidden);
      partnerModalElement.removeEventListener(
        "hidden.bs.modal",
        this.removeOverflowHidden
      );

      const qrModalElement = document.getElementById("qrModal");
      qrModalElement.removeEventListener("show.bs.modal", this.addOverflowHidden);
      qrModalElement.removeEventListener(
        "hidden.bs.modal",
        this.removeOverflowHidden
      );
    }
  },

  methods: {
    onResize() {
      this.isLaptop = window.innerWidth >= 1024;
    },
    // once: the state is cleared right away, so a reload or Back/Forward does not show it again
    openEvalSubmitted() {
      const data = window.history.state?.evalSubmitted;
      if (!data) return;
      window.history.replaceState({ ...window.history.state, evalSubmitted: null }, "");
      this.evalSubmitted = data;
      disableScroll();
      Modal.getOrCreateInstance(this.$refs.evalSubmittedModal, { backdrop: "static", keyboard: false }).show();
      // "Quote Q-…" in the message (no loader: the modal is already open)
      evaluationApi.quote(this.$route.params.id, { silent: true }).then((res) => {
        if (res.ok && res.body?.quote_number) this.evalSubmitted = { ...this.evalSubmitted, quoteNumber: res.body.quote_number };
      });
    },
    closeEvalSubmitted() {
      Modal.getInstance(this.$refs.evalSubmittedModal)?.hide();
      enableScroll();
    },

    addOverflowHidden() {
      document.documentElement.classList.add("overflow-hidden");
    },
    removeOverflowHidden() {
      document.documentElement.classList.remove("overflow-hidden");
    },

    ...mapActions([
      "getBookingDetail",
      "getBookingNotes",
      "getQRData",
      "getBookingDiagnosisItems",
      "storeChecks",
      "updateJobStatus",
      "resumeJob",
      "endJob",
      "getMarkAsCompleteObject",
      "resubmitQuote",
      "getBookingDetails",
      "createInvoice"
    ]),

    cancelCallback() {
      this.modal.hide();
      this.getBookingDetail({
        booking_id: this.$route.params.id,
      });
    },

    getData() {
      this.getBookingDetail({
        booking_id: this.$route.params.id,
      });
    },

    setQR() {
      if (this.is_QR) {
        this.getQRData({
          job_id: this.$route.params.id,
          job_source: "sar",
        });
      }
    },

    getNotesData() {
      this.getBookingNotes({
        booking_id: this.$route.params.id,
      });
    },

    getJobStatusWithDescriptionForJobStatus(type) {
      let description = "";
      let actions = [];
      let status = this.jobDetail.status;
      this.is_QR = false;
      this.isDeclineQuoteEnable = false;
      this.isConsumerQuoteDeclined = false;
      this.showCreateVerbage = false;

      // job of another technician: only the view actions Android shows (setReadOnlyJobButtons)
      if (this.isReadOnly && type != "description") {
        return this.getReadOnlyActions(status);
      }

      //1 : accepted
      if (status == 1) {
        actions.push(this.makeAction("On My Way"));
        actions.push(this.makeAction("Cancel Job"));
        description = "Time to check the unit and identify the fault.";
      }
      // 2: en_route
      else if (status == 2) {
        actions.push(this.makeAction("Arrived"));
        actions.push(this.makeAction("Cancel Job"));
        description = "Time to check the unit and identify the fault.";
      }
      //3 : arrived
      else if (status == 3) {
        this.showCreateVerbage = true;
        actions.push({
          name: "Start Evaluation",
          originalImg: require("../../assets/images/start-evaluation-icon.png"),
          hoverImg: require("../../assets/images/start-evaluation-hover-icon.png"),
        });
        description = "Time to check the unit and identify the fault.";
      }
      //4 :diagnosis_started
      else if (status == 4) {
        actions.push({
          name: "Resume Evaluation",
          originalImg: require("../../assets/images/resume-evaluation-icon.png"),
          hoverImg: require("../../assets/images/resume-evaluation-hover-icon.png"),
        });
        if (this.jobDetail.is_partner_job == '0' || !this.jobDetail.depp_schedule?.is_allied) {
          this.isDeclineQuoteEnable = true;
        }
        description =
          "Evaluation in progress. Create a quote for consumer's approval.";
      }
      //5 :diagnosis_completed
      else if (status == 5) {
        actions.push({
          name: "View Evaluation",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        actions.push({
          name: "Suspend Job",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });

        actions.push({
          name: "Invoices",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        if (this.hasInstallationPricing) actions.push(this.makeAction("Installation Pricing"));
        if (this.jobDetail.is_greensky) actions.push(this.makeAction("GreenSky®"));

        this.isDeclineQuoteEnable = true;
        this.is_QR = true;

        description =
          (this.jobDetail.consumer?.first_name || 'The consumer') +
          " is approving the quote. Look out for them; they may need your help.";
      }

      //6: quote_ready
      else if (status == 6) {
        actions.push(this.makeAction("Begin Job"));
        actions.push({
          name: "Approved Quote",
          originalImg: require("../../assets/images/approved-quote-icon.png"),
          hoverImg: require("../../assets/images/approved-qoute-hover-icon.png"),
        });
        actions.push({
          name: "View Evaluation",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        actions.push({
          name: "Invoices",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });

        if (this.hasInstallationPricing) actions.push(this.makeAction("Installation Pricing"));

        if (this.jobDetail.is_partner_job == '0' || !this.jobDetail.depp_schedule?.is_allied) {
          this.isDeclineQuoteEnable = true;
        }
        this.is_QR = true;

        description =
          "Congratulations! The customer has approved the quote. Start fixing the unit as per the approved quote.";
      }

      //7: started
      else if (status == 7) {
        actions.push({
          name: "Suspend Job",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        actions.push({
          name: "End Job",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        if (this.jobDetail.is_partner_job == '0' || !this.jobDetail.depp_schedule?.is_allied) {
          this.isDeclineQuoteEnable = true;
        }
        actions.push({
          name: "Approved Quote",
          originalImg: require("../../assets/images/approved-quote-icon.png"),
          hoverImg: require("../../assets/images/approved-qoute-hover-icon.png"),
        });
        actions.push({
          name: "View Evaluation",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        actions.push({
          name: "Invoices",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        if (this.hasInstallationPricing) actions.push(this.makeAction("Installation Pricing"));
        this.is_QR = true;
        description = "Repair works in progress…";
      }

      //8: suspended
      else if (status == 8) {
        actions.push({
          name: "Resume Job",
          originalImg: require("../../assets/images/resume-job-icon.png"),
          hoverImg: require("../../assets/images/resume-job-hover-icon.png"),
        });

        if (this.jobDetail.is_partner_job == '0' ||
          !(
            this.jobDetail.depp_schedule != null &&
            this.jobDetail.depp_schedule?.is_allied == 1 &&
            this.jobDetail.is_diagnosis_approved
          )
        ) {
          this.isDeclineQuoteEnable = true;
        }

        if (this.jobDetail.is_diagnosis_approved) {
          actions.push({
            name: "Approved Quote",
            originalImg: require("../../assets/images/approved-quote-icon.png"),
            hoverImg: require("../../assets/images/approved-qoute-hover-icon.png"),
          });
        }
        actions.push({
          name: "View Evaluation",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        actions.push({
          name: "Invoices",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        if (this.hasInstallationPricing) actions.push(this.makeAction("Installation Pricing"));
        this.is_QR = true;
        description =
          'This job is in progress but under suspended mode. Hit "Resume Job" to start working on it again!';
      }

      //9: completed
      else if (status == 9) {
        actions.push({
          name: "Approved Quote",
          originalImg: require("../../assets/images/approved-quote-icon.png"),
          hoverImg: require("../../assets/images/approved-qoute-hover-icon.png"),
        });
        // Android: paid_status == 0 → Checkout, any other value → Invoices
        if (Number(this.jobDetail.paid_status) === 0) {
          actions.push({
            name: "Checkout",
            originalImg: require("../../assets/images/checkout-icon.png"),
            hoverImg: require("../../assets/images/checkout-hover-icon.png"),
          });
        }
        else {
          actions.push({
            name: "Invoices",
            originalImg: require("../../assets/images/edit-evaluation-icon.png"),
            hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
          });
        }
        actions.push({
          name: "View Evaluation",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });

        if (this.hasInstallationPricing) actions.push(this.makeAction("Installation Pricing"));
        this.is_QR = true;
        description = "This job has been completed. Get ready for another job.";
      }

      //10: cancelled
      else if (status == 10) {
        description = "Job has been cancelled.";
      }

      //11: rejected
      // else if (status == 11) {

      // }

      //13: new_system
      else if (status == 13) {
        actions.push({
          name: "View Order",
          originalImg: require("../../assets/images/view-order-icon.png"),
          hoverImg: require("../../assets/images/view-order-hover-icon.png"),
        });
        actions.push({
          name: "View Evaluation",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        actions.push({
          name: "Invoices",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        if (this.hasInstallationPricing) actions.push(this.makeAction("Installation Pricing"));
        // actions.push("QR Code");
        this.is_QR = true;
      }

      //16 : diagnossis_fee_ppending
      else if (status == 16) {
        actions.push({
          name: "View Evaluation",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        // if (this.jobDetail.replacement_quote.is_consumer_requested_pricing) {
        //   actions.push("Installation Pricing");
        // }
        // actions.push("Job History");
        // actions.push("QR Code");
        this.is_QR = true;
        // isConsumerDeclined = true
        description = "";
      }

      //14:consumer_quote_decline
      else if (status == 14) {
        actions.push({
          name: "View Evaluation",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        actions.push({
          name: "End Job",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        actions.push({
          name: "Resubmit Quote",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        actions.push({
          name: "Invoices",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });
        // Android shows QR Code here too (when actions.can_share_quote)
        this.is_QR = true;
        this.isConsumerQuoteDeclined = true;
        description = "Quote has been declined by consumer.";
      }


      // 


      // if (this.jobDetail.invoice_count > 0) {
      //   actions.push("View Invoice");
      // }


      //Check for edit evaluation
      // Android: diagnosis_editable && actions.can_edit_diagnosis
      if (this.jobDetail.diagnosis_editable && this.jobDetail.actions?.can_edit_diagnosis) {

        actions.splice(2, 0, {
          name: "Edit Evaluation",
          originalImg: require("../../assets/images/edit-evaluation-icon.png"),
          hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
        });

      }

      //Check for Decline Quote
      if (!this.jobDetail.actions?.can_decline_job) {
        this.isDeclineQuoteEnable = false
      }

      //Remove share Quote button
      if (!this.jobDetail.actions?.can_share_quote ?? true) {

        this.is_QR = false;

      }

      // Android setJobHistoryButton(): every status listed above except New System (13) and Cancelled (10)
      if (this.hasJobHistory(status)) actions.push(this.makePlainAction("Job History"));
      // Android: "Universal Quotes" (quotes created from this job) for non super pros
      if (!this.isFromJobHistory && !this.isSuperPro) actions.push(this.makePlainAction("Universal Quotes"));

      // Android: extra "Cancel Job" for super pros after arrival (not partner/NRG jobs) and for child jobs (3-5)
      const hasCancel = actions.some(a => this.actionName(a) === "Cancel Job");
      if (!hasCancel && status > 2 && this.isSuperPro && status != 10) {
        if (!(this.jobDetail.depp_schedule && this.jobDetail.depp_schedule.status)) {
          actions.push(this.makeAction("Cancel Job"));
        }
      } else if (!hasCancel && [3, 4, 5].includes(Number(status)) && this.jobDetail.is_child) {
        actions.push(this.makeAction("Cancel Job"));
      }

      if (this.jobDetail.is_send_proposal == 1) {
        actions.push("Send Proposal");
      }

      // Android title-bar ⋮ "View Linked Jobs" (here a normal action; lands in ⋮ when there are more than 3)
      if (this.linkedJobs.length) actions.push(this.makePlainAction("View Linked Jobs"));

      // payment acknowledgement (Android PaymentInfoDialog) stays reachable while it is required
      if (this.isPaymentAckRequired) {
        actions.unshift(this.makeAction("Payment Acknowledgement"));
      }

      if (type == "description") {
        return description;
      } else {
        return actions;
      }
    },

    // Android extra buttons that are not job-status actions (no icon); ActionBar decides pill vs ⋮
    makePlainAction(name) {
      return { name };
    },
    showBsModal(id) {
      const el = document.getElementById(id);
      if (el) Modal.getOrCreateInstance(el).show();
    },

    hasJobHistory(status) {
      return !this.isFromJobHistory && [1, 2, 3, 4, 5, 6, 7, 8, 9, 14].includes(Number(status));
    },

    makeAction(name) {
      return {
        name,
        originalImg: require("../../assets/images/edit-evaluation-icon.png"),
        hoverImg: require("../../assets/images/edit-evaluation-hover-icon.png"),
      };
    },

    // Android JobLandingFragment.setReadOnlyJobButtons (Job Notes / Job History / Gallery are on the page already)
    getReadOnlyActions(status) {
      const s = Number(status);
      const actions = [];
      if (s === 13) actions.push(this.makeAction("View Order"));
      if ([6, 7, 9].includes(s) || (s === 8 && this.jobDetail.is_diagnosis_approved)) {
        actions.push({
          name: "Approved Quote",
          originalImg: require("../../assets/images/approved-quote-icon.png"),
          hoverImg: require("../../assets/images/approved-qoute-hover-icon.png"),
        });
      }
      if ([5, 6, 7, 8, 9, 13, 14].includes(s)) {
        actions.push(this.makeAction("View Evaluation"));
        this.is_QR = !!this.jobDetail.actions?.can_share_quote;
      }
      if (this.hasJobHistory(s)) actions.push(this.makePlainAction("Job History"));
      if (this.linkedJobs.length) actions.push(this.makePlainAction("View Linked Jobs"));
      this.isConsumerQuoteDeclined = s === 14;
      return actions;
    },

    getJobStatusDescription() {
      let description = "";
      let status = this.jobDetail.status;
      //1 : accepted
      if (status == 1) {
        description = "Time to check the unit and identify the fault.";
      }
      // 2: en_route
      else if (status == 2) {
        description = "Time to check the unit and identify the fault.";
      }
      //3 : arrived
      else if (status == 3) {
        description = "Time to check the unit and identify the fault.";
      }
      //4 :diagnosis_started
      else if (status == 4) {
        description =
          "Evaluation in progress. Create a quote for consumer's approval.";
      }
      //5 :diagnosis_completed
      else if (status == 5) {
        description =
          (this.jobDetail.consumer?.first_name || 'The consumer') +
          " is approving the quote. Look out for them; they may need your help.";
      }
      //6: quote_ready
      else if (status == 6) {
        description =
          "Congratulations! The customer has approved the quote. Start fixing the unit as per the approved quote.";
      }
      //7: started
      else if (status == 7) {
        description = "Repair works in progress…";
      }
      //8: suspended
      else if (status == 8) {
        description =
          'This job is in progress but under suspended mode. Hit "Resume Job" to start working on it again!';
      }
      //9: completed
      else if (status == 9) {
        description = "This job has been completed. Get ready for another job.";
      }
      //10: cancelled
      else if (status == 10) {
        description = "Job has been cancelled.";
      }
      //11: rejected
      // else if (status == 11) {
      // }
      //13: new_system
      // else if (status == 13) {
      // }
      //16 : diagnossis_fee_ppending
      else if (status == 16) {
        description = "";
      }
      //14:consumer_quote_decline
      else if (status == 14) {
        description = "Quote has been declined by consumer.";
      }
      return description;
    },

    // actions may be objects ({name, originalImg}) or plain strings ("Send Proposal")
    actionName(item) {
      return typeof item === 'string' ? item : item?.name;
    },
    actionColor(item) {
      const name = this.actionName(item);
      if (['Suspend Job'].includes(name)) return '#774360';
      if (['Checkout'].includes(name)) return '#2563EB';
      if (['Resume Job'].includes(name)) return '#06B6D4';
      if (['Resume Evaluation'].includes(name)) return '#EA8A8A';
      if (['Start Evaluation'].includes(name)) return '#6987EB';
      if (['Edit Evaluation'].includes(name)) return '#F67280';
      if (['End Job'].includes(name)) return '#A793DB';
      if (['View Order'].includes(name)) return '#6366F1';
      if (['Invoices'].includes(name)) return '#4A628A';
      if (['Resubmit Quote'].includes(name)) return '#00B7C2';
      if (['Approved Quote'].includes(name)) return '#1FA971';
      if (['View Evaluation'].includes(name)) return '#6987EB';
      if (name === 'Universal Quotes') return '#2A7BB8';
      if (name === 'Job History') return '#9C9BBF';
      if (name === 'Cancel Job') return '#DC2626';
      if (name === 'On My Way') return '#FC819E';
      if (name === 'Arrived') return '#14B8A6';
      if (['Send Proposal', 'Begin Job', 'Payment Acknowledgement'].includes(name)) return '#F67280';
      return '#4E7BFF';
    },
    actionClicked(action) {
      // Android: from Arrived, Start Evaluation first sets Evaluation Started (4), otherwise
      // diagnosis/submit/collect-data rejects the evaluation (QA D-33)
      if (action == "Start Evaluation") {
        this.$dialog.show({
          title: "Job Status",
          text: "Are you sure to change job status to Evaluation Started?",
          buttonText: "Yes",
          onConfirm: async () => {
            const res = await bookingActionsApi.updateStatus(this.$route.params.id, BOOKING_STATUS.DIAGNOSIS_STARTED);
            if (!res.ok) return this.handleFailure(res);
            toast.success(res.message || "Evaluation has been started successfully.");
            this.$router.push({ path: `/select/unit/${this.$route.params.id}` });
          },
        });
      } else if (action == "Resume Evaluation") {
        this.$router.push({
          path: `/select/unit/${this.$route.params.id}`,
        });
      } else if (action == "Edit Evaluation") {
        this.$router.push({
          path: `/select/unit/${this.$route.params.id}`,
        });
        // this.storeChecks({
        //   booking_id: this.$route.params.id,
        //   diagnosis_type: "edit_diagnosis_items",
        //   diagnosis_data: JSON.stringify(null),
        // });
      } else if ((action == "View Evaluation" || action == "Approved Quote") && !this.isReadOnly) {
        // Figma View Evaluation page (Approved Quote → its Approve Quote tab); the consumer email rides in the
        // history state for Share Quote, never in the URL
        this.$router.push({
          path: `/job/evaluation/${this.$route.params.id}`,
          query: action == "Approved Quote" ? { tab: "approve" } : {},
          state: { consumerEmail: this.jobDetail.consumer?.email || "" },
        });
      } else if (action == "View Evaluation") {
        // Android: another technician's job opens the summary without prices (?status=1&price=0)
        this.techUrl = this.jobDetail.technician_diagnostic_url + "/?status=1&price=0";
        this.modal = new Modal(this.$refs.iframeModal);
        this.modal.show();
      } else if (action == "Approved Quote") {
        this.techUrl = this.jobDetail.technician_diagnostic_url + "/?status=0";
        this.modal = new Modal(this.$refs.iframeModal);
        this.modal.show();
      } else if (action == "Suspend Job") {
        this.suspendReason = '',
          this.isValidate = false
        this.modal = new Modal(this.$refs.suspendJobModal);
        this.modal.show();
      } else if (action == "Resume Job") {
        this.$dialog.show({
          title: "Resume Job",
          text: "Are you sure you want to resume this job?",
          onConfirm: () => {
            this.resumeJob({
              booking_id: this.$route.params.id,
            });
          },
        });
      } else if (action == "End Job") {
        if (this.jobDetail.status == 14) {
          this.declineCallButtonClicked();
        } else {
          this.$dialog.show({
            title: "End Job",
            text: "Are you sure you want to end this job?",
            onConfirm: () => {
              this.endJob({
                booking_id: this.$route.params.id,
              });
            },
          });
        }
      } else if (action == "Resubmit Quote") {
        this.$dialog.show({
          title: "Resubmit Quote",
          text: "Are you sure you want to resubmit this quote to consumer?",
          onConfirm: () => {
            this.resubmitQuote({
              booking_id: this.$route.params.id,
            });
          },
        });
      } else if (action == "Checkout") {
        // this.createInvoice({
        //   job_id: this.$route.params.id,
        //   job_type: 'sar'
        // });
        this.$router.push({
          path: `/job/invoice/${this.$route.params.id}`,
        });
      } else if (action == "Invoices") {
        this.$router.push({
          path: `/job/invoice/${this.$route.params.id}`,
        });
      } else if (action == "View Order") {
        // Android: bookingView.order_id (BookingDetailResource meta order_id, 0 when none)
        const orderId = Number(this.jobDetail.order_id) || Number(this.jobDetail.replacement_quote?.id) || 0;
        if (orderId) {
          this.$router.push({ path: `/replacement-quote/detail/${orderId}` });
        } else {
          toast.error("No order is linked to this job.");
        }
      } else if (action == "Send Proposal") {
        this.$refs.sendProposalModal?.open(this.jobDetail.consumer?.email || "");
      } else if (action == "On My Way") {
        this.confirmStatus("Are you sure you want to mark the En-Route status.", () => bookingActionsApi.enRoute(this.$route.params.id), "Job has been marked as en route.");
      } else if (action == "Arrived") {
        this.confirmStatus("Are you sure you want to mark the Arrived status.", () => bookingActionsApi.arrived(this.$route.params.id), "Job has been marked as arrived.");
      } else if (action == "Cancel Job") {
        this.confirmStatus("Are you sure to change Job status to Booking Status Cancelled?", () => bookingActionsApi.cancel(this.$route.params.id), "Job has been cancelled successfully.");
      } else if (action == "Begin Job") {
        this.confirmStatus("Are you sure to change Job status to Started?", () => bookingActionsApi.begin(this.$route.params.id), "Job has been started successfully.");
      } else if (action == "Payment Acknowledgement") {
        this.$refs.paymentAckModal?.open();
      } else if (action == "Installation Pricing") {
        // Android getStaticUrls (sales/presentation?booking_id) → SalesItemListFragment with this booking
        this.$router.push({ path: "/system-designer", query: { booking_id: this.$route.params.id } });
      } else if (action == "GreenSky®") {
        const name = this.capitalizeFirst(this.jobDetail.consumer?.first_name || "");
        this.$dialog.show({
          title: "Alert",
          text: `${name} has applied for GreenSky financing. After ${name} receives the shopping pass, you will be required to authorize the transactions for the shopping pass. Please consult with ${name} and then press "Proceed" to visit the GreenSky Merchant portal or press "Cancel" to return`,
          buttonText: "Proceed",
          onConfirm: () => window.open("http://portal.greensky.com/", "_blank", "noopener"),
        });
      } else if (action == "Job History") {
        this.$refs.jobHistoryModal?.open();
      } else if (action == "Universal Quotes") {
        if (!this.jobDetail.consumer) {
          toast.error("Consumer does not exist.");
          return;
        }
        this.$router.push({
          path: "/work-order/universal-quotes",
          query: { source_id: this.$route.params.id, source_type: "sar" },
        });
      } else if (action == "View Linked Jobs") {
        if (this.linkedJobs.length === 1) {
          this.openLinkedJob(this.linkedJobs[0].booking_id);
        } else if (this.linkedJobs.length > 1) {
          this.modal = new Modal(this.$refs.linkedJobsModal);
          this.modal.show();
        }
      }
    },

    // Android DialogHelper "Job Status" confirmation → status API → reload the job
    // successText: the toast when the API sends no message
    confirmStatus(text, call, successText) {
      this.$dialog.show({
        title: "Job Status",
        text,
        buttonText: "Yes",
        onConfirm: async () => {
          const res = await call();
          if (res.ok) this.onActionSuccess(res.message || successText);
          else this.handleFailure(res);
        },
      });
    },

    // linked job <router-link> navigates; a plain click also closes the "Select Linked Job" modal
    onLinkedJobLinkClick(e) {
      if (!isNewTabClick(e)) Modal.getInstance(this.$refs.linkedJobsModal)?.hide();
    },

    openLinkedJob(id) {
      Modal.getInstance(this.$refs.linkedJobsModal)?.hide();
      if (String(id) === String(this.$route.params.id)) return;
      this.$router.push(this.linkedJobRoute(id));
    },

    // a linked job remembers where it was opened from (?from_job) for BackToDetail's "Back to Service & Repair <id>"; the mobile flag is kept
    linkedJobRoute(id) {
      const { is_from_mobile } = this.$route.query;
      return { path: `/job/detail/${id}`, query: { from_job: this.$route.params.id, ...(is_from_mobile ? { is_from_mobile } : {}) } };
    },

    capitalizeFirst(str) {
      return str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
    },

    // Android: completed job with paid_status == 0 → "Unpaid Invoice(s)" → Yes opens the invoices (job/invoice/{id})
    autoShowUnpaidAlert() {
      const id = this.$route.params.id;
      if (this.isReadOnly || String(this.jobDetail.id) !== String(id)) return;
      if (Number(this.jobDetail.status) !== BOOKING_STATUS.COMPLETED || Number(this.jobDetail.paid_status) !== 0) return;
      if (this.unpaidAlertShownFor === id) return;
      this.unpaidAlertShownFor = id;
      this.$dialog.show({
        title: "Unpaid Invoice(s)",
        text: "This job has one or more unpaid invoices. Would you like to make the payment now?",
        buttonText: "Yes",
        onConfirm: () => this.$router.push({ path: `/job/invoice/${id}` }),
      });
    },

    acceptJob() {
      this.confirmStatus("Are you sure to change job status to Accepted?", () => bookingActionsApi.accept(this.$route.params.id), "Job has been accepted successfully.");
    },

    rejectJob() {
      this.$dialog.show({
        title: "Job Status",
        text: "Are you sure to change job status to Rejected?",
        buttonText: "Yes",
        onConfirm: async () => {
          const res = await bookingActionsApi.reject(this.$route.params.id);
          if (res.ok) {
            toast.success(res.message || "Job has been rejected.");
            this.$router.push({ path: "/work-order/service-and-repair" });
          } else {
            this.handleFailure(res);
          }
        },
      });
    },

    onActionSuccess(message, reload = true) {
      if (message) toast.success(message);
      if (reload) this.getData();
    },

    // single-active-record guard → offer to open the active job; otherwise toast the API message
    handleFailure(res) {
      const guard = res?.activeRecord;
      const record = guard?.active_record;
      if (record) {
        this.$dialog.show({
          title: "Dispositioned Leads",
          text: guard.message || res.message,
          buttonText: record.button_text || "Resolve",
          onConfirm: () => {
            if (record.type === "appointment") this.$router.push(`/appointment/detail/${record.id}`);
            else if (record.type === "order") this.$router.push(`/replacement-quote/detail/${record.id}`);
            else if (record.type === "booking") this.$router.push(`/job/detail/${record.id}`);
          },
        });
        return;
      }
      toast.error(res?.message || "Something went wrong. Please try again.");
    },

    onPaymentAckFailed(res) {
      this.handleFailure(res);
      // Android refreshes the job after a failed acknowledgement too
      if (!res?.activeRecord) this.getData();
    },

    // Android: GETPAYMENTDATA when acknowledgement == REQUIRED, once per job load
    autoShowPaymentAck() {
      const id = this.$route.params.id;
      if (!this.isPaymentAckRequired || this.isReadOnly || String(this.jobDetail.id) !== String(id)) return;
      if (this.paymentAckShownFor === id) return;
      this.paymentAckShownFor = id;
      this.$nextTick(() => this.$refs.paymentAckModal?.open({ silentEmpty: true }));
    },

    declineCallButtonClicked() {
      this.getMarkAsCompleteObject({
        booking_id: this.$route.params.id,
      });
      document.body.style.overflow = "hidden";
    },

    cancelBooking() {
      // Implement the cancellation logic here
      alert("Booking cancelled");
    },

    getCapitalize(str) {
      let res = "" + str;
      return res
        .toLowerCase()
        .split(" ")
        .map(function (i) {
          if (i.length > 0) {
            return i.charAt(0).toUpperCase() + i.substr(1);
          }

          return i;
        })
        .join(" ");
    },

    suspendJobBtn() {
      if (this.$helper.isEmpty(this.suspendReason)) {
        this.isValidate = true
      } else {
        this.isValidate = false
        this.updateJobStatus({
          booking_id: this.$route.params.id,
          status: "8",
          reason: this.suspendReason
        });
      }
    }
  },
};
</script>

<style>
.cancelBookingOption .form-group [type="radio"]:checked+label,
.cancelBookingOption .form-group [type="radio"]:not(:checked)+label {
  padding-left: 2rem;
  padding-right: 0;
}

.cancelBookingOption .form-group [type="radio"]:checked+label:before,
.cancelBookingOption .form-group [type="radio"]:not(:checked)+label:before {
  left: 5px;
  width: 23px;
  height: 23px;
  top: -3px;
}

.cancelBookingOption .form-group [type="radio"]:checked+label:after,
.cancelBookingOption .form-group [type="radio"]:not(:checked)+label:after {
  left: 9px;
  top: 1px;
}

.cancelBookingOption .form-control {
  width: 150px;
  font-size: 18px;
  border-color: #092e87;
  height: 35px;
}

#iframeModal iframe {
  height: 75vh;
  width: 100%;
  overflow-x: hidden;
}

#iframeModal .closebutton,
#qrModal .closebutton,
#partnerModal .closebutton,
#profileModal .closebutton {
  filter: none;
  border: 0;
  opacity: 1;
}

#iframeModal .closebutton button,
#qrModal .closebutton button,
#partnerModal .closebutton button,
#profileModal .closebutton button {
  border: 1px solid #000;
  border-radius: 50%;
  padding: 0.5rem;
  opacity: 1;
}

.allActionButtons span {
  width: 49%;
}

.allActionButtons span button {
  display: block;
  width: 100%;
}

.booking .dropdown-menu li img {
  width: 20px;
  height: 20px;
  object-fit: cover;
}

.booking .dropdown-menu {
  width: 180px;
}

#evalSubmittedModal .fxModalHeader {
  box-shadow: none !important;
}
</style>
